import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import $ from 'jquery';

import CptAlertBox from 'components/cpt_alert_box';
import CheckedList, { SelectionState } from 'components/checked_list';
import Notifications from 'lib/manage/notifications';
import PaginationControl from 'components/pagination_control';
import Pill from 'components/pill';
import PillGroup from 'components/pill_group';
import { SortableListHeader, SortableListHeaders } from 'components/sortable_list';

import { Histogram } from './types';
import NotificationsSubscriberRow from './notifications_subscriber_row';
import SubscriberAction from './subscriber_action';
import SubscriberQuery from './subscriber_query';

import { inflect } from 'inflection';

/**
 * @constant {Array} The list of subscribers states of which the front-end is aware.
 */
const SUBSCRIBER_STATES = ['active', 'quarantined', 'unconfirmed'];

/**
 *  The interface to browse and search lists of email, sms, and web subscribers.
 */
export default class NotificationsSubscriberTypeManager extends Component {
  static propTypes = {
    pageCode: PropTypes.string.isRequired, // The code of the page whose subscribers to list.
    limit: PropTypes.number.isRequired, // The number of subscribers to display per-page.
    type: PropTypes.oneOf(['email', 'sms', 'webhook']).isRequired,
    allTimeHistogram: PropTypes.shape({
      email: Histogram,
      sms: Histogram,
      webhook: Histogram,
    }).isRequired,
    thisMonthHistogram: PropTypes.shape({
      email: Histogram,
      sms: Histogram,
      webhook: Histogram,
    }).isRequired,
    touch: PropTypes.func, // The function to call to indicate that the server should be re-queried.
    updatedAt: PropTypes.string.isRequired,
  };

  state = {
    subscriberState: 'active',
    sortField: 'primary',
    sortDirection: 'asc',
    total: 0,
    page: 0,
    selection: SelectionState.clear(),
    confirmationModal: null,
  };

  componentDidUpdate(_, oldState) {
    if (this.state.confirmationModal && oldState.confirmationModal !== this.state.confirmationModal) {
      $(`#modal-confirmation-${this.state.confirmationModal.props.id}`).modal();
    }
  }

  _handleClearSelection = (shouldTrack) => {
    if (shouldTrack) {
      analytics.track('Notifications Page - Clear Selection', {
        source: 'Notifications Page',
        state: this.state.subscriberState,
        type: this.props.type,
      });
    }
    this.setState({ selection: SelectionState.clear() })
  };

  _handleSelectAll = () => {
    analytics.track('Notifications Page - Select All', {
      source: 'Notifications Page',
      state: this.state.subscriberState,
      type: this.props.type,
    });
    this.setState({ selection: SelectionState.selectAll() });
  }

  _removeSubscriber = (e, subscriberCode, state) => {
    e.preventDefault();

    let subscriberCodes = this.state.selection.toJSON();
    if (Array.isArray(subscriberCode)) {
      subscriberCodes = subscriberCode;
    }

    const message = `Are you sure you want to remove ${
        subscriberCodes.length === 1 ? 'this subscriber?' : 'these subscribers?'}`;
    const params = {
      subscribers: subscriberCodes,
      type: this.props.type,
      state,
    };

    const confirmationModal = (
      <ConfirmModal
        id={`remove-subscriber-${this.state.subscriberState}`}
        confirm_text="Confirm Removal"
        message={message}
        title="Confirm Remove Subscriber"
        confirmClass="style-status-critical"
        onConfirm={() => {
          analytics.track('Notifications Page - Subscriber Unsubscribed', {
            source: 'Notifications Page',
            state: this.state.subscriberState,
            type: this.props.type,
            count: subscriberCodes === 'all' ? -1 : subscriberCodes.length,
          });
          Subscriber.unsubscribeSubscribers(this.props.pageCode, params).then(() => {
            HRB.utils.notify('Unsubscribe succeeded.');
            this._handleClearSelection(false);
            this.props.touch();
            this.setState({confirmationModal: null});
          }, () => {
            HRB.utils.notify('Unsubscribe failed.');
          });
        }}
        onCancel={() => this.setState({confirmationModal: null})} />
    );

    this.setState({
      confirmationModal: confirmationModal,
    });
  }

  _reactivateSubscriber = (e, subscriberCode) => {
    e.preventDefault();

    let subscriberCodes = this.state.selection.toJSON();
    if (Array.isArray(subscriberCode)) {
      subscriberCodes = subscriberCode;
    }

    const message = `Are you sure you want to reactivate ${
      subscriberCodes.length === 1 ? 'this subscriber?' : 'these subscribers?'}`;

    const confirmationModal = (
      <ConfirmModal
        id={`reactivate-subscriber-${this.state.subscriberState}`}
        confirm_text="Confirm Reactivate"
        message={message}
        title="Confirm Reactivate Subscriber"
        onConfirm={() => {
          analytics.track('Notifications Page - Subscriber Reactivated', {
            source: 'Notifications Page',
            state: this.state.subscriberState,
            type: this.props.type,
            count: subscriberCodes === 'all' ? -1 : subscriberCodes.length,
          });
          Subscriber.reactivateSubscribers(this.props.pageCode, { subscribers: subscriberCodes }).then(() => {
            HRB.utils.notify('Reactivate succeeded.');
            this._handleClearSelection(false );
            this.props.touch();
            this.setState({confirmationModal: null});
          }, () => {
            HRB.utils.notify('Reactivate failed.');
          });
        }}
        onCancel={() => this.setState({confirmationModal: null})} />
    );

    this.setState({
      confirmationModal: confirmationModal,
    });
  }

  _resendConfirmationSubscriber = (e, subscriberCode) => {
    e.preventDefault();

    let subscriberCodes = this.state.selection.toJSON();
    if (Array.isArray(subscriberCode)) {
      subscriberCodes = subscriberCode;
    }

    const message = `Are you sure you want to resend a confirmation message to ${
      subscriberCodes.length === 1 ? 'this subscriber?' : 'these subscribers?'}`;

    const successMessage = `The confirmation email was successfully sent to the ${
        inflect('subscriber', subscriberCodes.length)}`;

    const confirmationModal = (
      <ConfirmModal
        id={`resend-confirmation-subscriber-${this.state.subscriberState}`}
        confirm_text="Confirm Resend"
        message={message}
        title="Confirm Resend Confirmation"
        onConfirm={() => {
          Subscriber.resendConfirmationSubscribers(this.props.pageCode, subscriberCodes).then(() => {
            analytics.track('Notifications Page - Subscriber Confirmation Resent', {
              source: 'Notifications Page',
              state: this.state.subscriberState, // Should always be 'unconfirmed'.
              type: this.props.type, // Should always be 'email'.
              count: subscriberCodes === 'all' ? -1 : subscriberCodes.length,
            });
            HRB.utils.notify(successMessage);
            this._handleClearSelection(false);
            this.setState({confirmationModal: null});
          }, () => {
            HRB.utils.notify('Resend confirmation failed.');
          });
        }}
        onCancel={() => this.setState({confirmationModal: null})} />
    );

    this.setState({
      confirmationModal: confirmationModal,
    });
  }

  _changedSelectedState = (state) => {
    analytics.track('Notifications Page - Change Subscriber State Filter', {
      source: 'Notifications Page',
      state: state,
      type: this.props.type,
    });
    this.setState({ subscriberState: state, page: 0, selection: SelectionState.clear() });
  };

  render () {
    // Compute the relevant histogram data.
    const allTimeTotal =
      this.props.allTimeHistogram[this.props.type] &&
      this.props.allTimeHistogram[this.props.type].total;
    const addedThisMonth =
      this.props.thisMonthHistogram[this.props.type] &&
      this.props.thisMonthHistogram[this.props.type].total;

    // Fetch the total for pagination purposes for this subscriber state.
    const paginationTotal =
      this.props.allTimeHistogram[this.props.type] &&
      this.props.allTimeHistogram[this.props.type][this.state.subscriberState];

    const emptySubscriberType = allTimeTotal === 0;
    const emptySubscriberTypeInState = paginationTotal === 0;

    // The total label in the upper left of the tab pane.
    const pluralizedSubscribers = inflect('subscriber', allTimeTotal);
    const subscriberCountLabel = allTimeTotal !== undefined && addedThisMonth !== undefined
      ? (<div className="total-and-recent-subscriber-count">
          Total {allTimeTotal} {
            Notifications.SUBSCRIBER_SENTENCE_DISPLAY_TYPE_BY_TYPE[this.props.type]
          } {pluralizedSubscribers}, {addedThisMonth} added this month.
        </div>)
      : <div className="total-and-recent-subscriber-count" />;

    // Here are the state pills at the top right of the tab pane.
    // Not all types have all states; we should generate them dynamically.
    const activePillCountString = this.props.allTimeHistogram[this.props.type] ? ` (${this.props.allTimeHistogram[this.props.type].active})` : '';
    const unconfirmedPillCountString = this.props.allTimeHistogram[this.props.type] ? ` (${this.props.allTimeHistogram[this.props.type].unconfirmed})` : '';
    const quarantinedPillCountString = this.props.allTimeHistogram[this.props.type] ? ` (${this.props.allTimeHistogram[this.props.type].quarantined})` : '';
    const unconfirmedFilterState = this.props.type === 'email'
      ? <Pill className="red" value="unconfirmed">Unconfirmed{unconfirmedPillCountString}</Pill>
      : null;

    // Column headers.
    // Though we rely heavily on flex, we give base widths for columns.
    const createdAtHeaderSize = this.state.subscriberState === 'quarantined' ? 'created-at-small' : 'created-at-large';

    // Quarantine is only shown in the "quarantine" state.
    const quarantineHeader = this.state.subscriberState === 'quarantined'
      ? (<SortableListHeader className="quarantined-at" field="quarantined_at" key="quarantined_at" tooltip="When a subscriber has not been reactivated in 90 days of quarantine, they will be automatically unsubscribed.">
          Date Quarantined<span className="tooltip-base">?</span>
        </SortableListHeader>)
      : null;

    const actionsHeaderSize = this.state.subscriberState === 'unconfirmed' ? 'actions-large' : 'actions-small';

    // Links to other subscriber states that are not empty.
    const otherStatesLinks = emptySubscriberTypeInState
        ? SUBSCRIBER_STATES.filter(ss => ss !== this.state.subscriberState &&
            this.props.allTimeHistogram[this.props.type] &&
            this.props.allTimeHistogram[this.props.type][ss]).map((ss) => {
              return (<button type="button" className="other-subscriber-states-btn" key={ss} onClick={() => this._changedSelectedState(ss)}>{ss}</button>);
            })
        : [];
    const otherStatesMessage = otherStatesLinks.length
        ? <span>Check {otherStatesLinks.reduce((prev, curr) => [prev, ' or ', curr])} subscribers.</span>
        : null;

    // These are actions suitable for the header.
    const selectedCount = this.state.selection.allSelected
      ? paginationTotal
      : this.state.selection.selectedSet.size;
    const selectedLabel = selectedCount === 0
      ? null
      : <span className="subtle"> ({selectedCount})</span>;
    let actions = [
      <SubscriberAction
        className="remove"
        key="remove"
        onClick={this._removeSubscriber}
        subscriberState={this.state.subscriberState}>
        remove{selectedLabel}
      </SubscriberAction>,
    ];
    if (this.state.subscriberState === 'quarantined') {
      actions = Array.concat(
        actions,
        <SubscriberAction
          className="reactivate"
          key="reactivate"
          onClick={this._reactivateSubscriber}
          subscriberState={this.state.subscriberState}>
          reactivate{selectedLabel}
        </SubscriberAction>,
      );
    }
    if (this.state.subscriberState === 'unconfirmed') {
      actions = Array.concat(
        actions,
        <SubscriberAction
          className="resend-confirmation"
          key="resend-confirmation"
          onClick={this._resendConfirmationSubscriber}
          subscriberState={this.state.subscriberState}>
          resend confirmation{selectedLabel}
        </SubscriberAction>,
      );
    }

    return (
      <div className="notifications-subscriber-type-manager">
        {this.state.confirmationModal}
        {/* The controls and labels above the list. */}
        { emptySubscriberType ? null
            : (<div className="pane-heading">
              {subscriberCountLabel}
              <PillGroup
                className="pull-right colored-filters"
                onChange={this._changedSelectedState}
                value={this.state.subscriberState}>
                <Pill className="green" value="active">Active{activePillCountString}</Pill>
                <Pill className="red" value="quarantined">Quarantined{quarantinedPillCountString}</Pill>
                {unconfirmedFilterState}
              </PillGroup>
            </div>)}

        {/* This ensures only the list is re-rendered when fetching page of subscribers. */}
        {/* It also ensures we only do this when necessary, as props change. */}
        <SubscriberQuery
          pageCode={this.props.pageCode}
          limit={this.props.limit}
          page={this.state.page}
          sortField={this.state.sortField}
          sortDirection={this.state.sortDirection}
          subscriberType={this.props.type}
          subscriberState={this.state.subscriberState}
          updatedAt={this.props.updatedAt}>
          {(subscribers, loading, error) => {
            const subscriberTypeQualifier = emptySubscriberType ? '' : this.state.subscriberState;
            const emptyListState = subscribers.length !== 0
              ? null
              : (<div className="pane-header">
                <div className="empty-state">
                  <div className="header">
                    You currently don't have any {subscriberTypeQualifier} {
                      Notifications.SUBSCRIBER_SENTENCE_DISPLAY_TYPE_BY_TYPE[this.props.type]
                    } subscribers.
                  </div>
                  <div className="body">
                    <span>{otherStatesMessage}</span>
                  </div>
                </div>
              </div>);

            // Construct the state for displaying any errors that occur inside the query component.
            const errorState = error
              ? (<CptAlertBox
                type="error"
                header="An error occurred while trying to fulfill your request."
                body={error} />)
              : null;

            // Construct the list contents.
            const subscriberRows = subscribers.map(subscriber => (
              <NotificationsSubscriberRow
                subscriber={subscriber}
                pageCode={this.props.pageCode}
                key={subscriber.id}
                state={this.state.subscriberState}
                href={`/pages/${this.props.pageCode}/subscribers/${subscriber.id}`}>
                {this.state.selection.isClear() ? actions : null}
              </NotificationsSubscriberRow>
            ));

            // And the headers.
            let headers = null;
            // If nothing is selected, the table header displays as column headers.
            if (this.state.selection.isClear()) {
              headers = [
                <SortableListHeader className="subscriber-title" field="primary" key="primary">
                  <span>
                    {Notifications.SUBSCRIBER_TITLE_DISPLAY_TYPE_BY_TYPE[this.props.type]} Subscribers
                  </span>
                </SortableListHeader>,
                <SortableListHeader
                  className={classnames("created-at", createdAtHeaderSize)}
                  field="created_at"
                  key="created_at">
                  Date Added
                </SortableListHeader>,
                quarantineHeader,
                <SortableListHeader
                  className={classnames(
                    'actions',
                    actionsHeaderSize,
                    this.state.selection.isClear() ? null : 'actions',
                  )}
                  key="actions">
                  {this.state.selection.isClear() ? 'Actions' : actions}
                </SortableListHeader>,
              ];
            } else {
              // If something is selected, the table header displays information and actions available
              // for the selected subscribers.
              //
              // 'All Are Selected' may be encoded in two different ways:
              // 1) by the symbolic selection state "all"
              // 2) by a list of <page size> selected ids when <page size> === paginationTotal.
              // allChecked is true for both of these cases, otherwise false.
              const allChecked = selectedCount === paginationTotal;
              const selectAllHeader = allChecked
                ? (<div className="select-all-prompt" key="select-all-prompt">
                    All {selectedCount} subscribers are selected. <button className="link-like" onClick={() => this._handleClearSelection(true)}>
                      Clear selection.
                    </button>
                </div>)
                : (<div className="select-all-prompt" key="select-all-prompt">
                  <span>{selectedCount} selected. </span>
                  <button className="link-like" onClick={this._handleSelectAll}>
                    Select all {paginationTotal} {this.state.subscriberState} subscribers.
                  </button>
                </div>);
              headers = [
                selectAllHeader,
                <div className={classnames("actions", actionsHeaderSize)} key="bulk-actions">
                  {actions}
                </div>,
              ];
            }

            // Render the list itself, specifying selection state, headers, and content.
            return (
              <div>
                <CheckedList
                  selection={this.state.selection}
                  onSelect={selection => {
                    analytics.track('Notifications Page - Selected Subscribers', {
                      source: 'Notifications Page',
                      count: selection.getCount(),
                      state: this.state.subscriberState,
                      type: this.props.type,
                    });
                    this.setState({ selection });
                  }}
                  total={paginationTotal || 0}
                  loading={loading}
                  error={errorState}
                  empty={emptyListState}
                  headers={
                    <SortableListHeaders
                      field={this.state.sortField}
                      direction={this.state.sortDirection}
                      onClick={(sortField, sortDirection) => {
                        analytics.track('Notifications Page - User Clicked Header', {
                          source: 'Notifications Page',
                          state: this.state.subscriberState,
                          type: this.props.type,
                          column: sortField,
                          direction: sortDirection,
                        });
                        this.setState({
                          sortField,
                          sortDirection,
                          page: 0,
                          selection: SelectionState.clear(),
                        })}}>
                      {headers}
                    </SortableListHeaders>
                  }>
                  {subscriberRows}
                </CheckedList>
              </div>
            );
          }}
        </SubscriberQuery>

        {/* Finally, render the pagination control iff there are multiple pages. */}

        {paginationTotal === undefined || paginationTotal <= this.props.limit
          ? null
          : <PaginationControl
            page={this.state.page + 1}
            limit={this.props.limit}
            total={paginationTotal}
            onChange={page => {
              analytics.track('Notifications Page - User Navigated To Page', {
                source: 'Notifications Page',
                state: this.state.subscriberState,
                type: this.props.type,
                page_number: page,
              });
              this.setState({ page: page - 1 })
            }} />}
      </div>
    );
  }
}

module.exports = NotificationsSubscriberTypeManager;



// WEBPACK FOOTER //
// ./app/assets/javascripts/components/manage/notifications/notifications_subscriber_type_manager.jsx